import HomeIcon from "@mui/icons-material/HomeOutlined";
import BusinessIcon from "@mui/icons-material/AccountBalanceOutlined";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import HowToRegIcon from "@mui/icons-material/HowToRegOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBookOutlined";
import PeopleIcon from "@mui/icons-material/PeopleOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonthOutlined";
import AssessmentIcon from "@mui/icons-material/AssessmentOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailableOutlined";
import ClassIcon from "@mui/icons-material/ClassOutlined";
import SubjectIcon from "@mui/icons-material/AutoStoriesOutlined";
import AssignmentIcon from "@mui/icons-material/AssignmentOutlined";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import PsychologyIcon from "@mui/icons-material/PsychologyOutlined";
import ChildCareIcon from "@mui/icons-material/ChildCareOutlined";
import { isViewingCurrentYear } from "./schoolYear";

// Parent nodes with `children` are pure groups: they carry no `resolve` of
// their own. In the nav menus clicking a group only expands/drills into it,
// never navigates. Everywhere a group still needs a destination (search hits,
// bookmarks, the Home grid) it inherits the URL of its first navigable
// descendant via `navTarget` / `nodeUrl` below - that first child is the
// section's de-facto landing page. Leaf nodes carry their own `resolve`.
//
// Node `id`s are a persistence contract, not just a React key.
//
// Staff bookmarks (StaffPreference#bookmarks) store the node `id` string, never
// a URL. A tile is rendered by resolving that id back to its node and calling
// `resolve(ctx)` at click time, so the registry owns the live URL. Consequences:
//
//   - `resolve` (the route/URL) and `label` can change freely. Existing
//     bookmarks keep working and just pick up the new link/label.
//   - An `id` MUST stay stable for the life of the feature. Renaming or removing
//     an id silently orphans every saved bookmark pointing at it (the resolver
//     drops unknown ids), with no error and no migration. A bookmark disappears
//     ONLY in that case.
//
// So: rename routes, relabel items, restructure `resolve` as needed, but treat
// `id` as frozen. If a section genuinely must be retired, that is the deliberate
// way to expire its bookmarks. New ids should be descriptive and unique
// (`<section>-<leaf>`); never recycle an old id for a different destination.
//
// Each node's `visible` predicate is its permission/feature gate. The backend
// (pages_v2_helper#nav_permissions) precomputes one boolean per gated link into
// `appProps.permissions`, applying view-OR-manage for most links and manage-only
// for a few; the predicates here just read those booleans through `can(ctx,
// <key>)` (see below). A group's predicate gates the whole section; each leaf's
// predicate gates that single link. A node with no `visible` is always shown.
// Groups are additionally hidden when none of their children are visible (see
// nodeVisible), so a permitted-but-empty section never renders a dead header.
//
// `v1: true` marks a node whose route is served by the older frontend (a full
// page load); nodes without it are modern SPA routes (default), navigated in
// place. The nav menus read the resolved target's `v1` flag to choose between an
// in-place SPA navigation and a full page load.
//
// `matchPath` is an optional second resolver used only to decide which tab is
// the active one (see nodeMatchPath). It defaults to `resolve`, which is right
// whenever a node's landing URL is also the root of the routes it owns. Declare
// it when the landing URL is DEEPER than that root - a section whose page has
// its own sub-tabs must land on one of them, but the nav tab has to stay
// selected on all of them.
const v1Path = (path) => (ctx) => `/school/${ctx.schoolSlug}${path}`;
const v1YearPath =
  (path) =>
  ({ schoolSlug, yearId }) =>
    `/school/${schoolSlug}${path}${yearId ? `?school_year=${yearId}` : ""}`;

// Reads a server-resolved permission boolean from ctx.permissions. Missing map
// (e.g. appProps not yet hydrated, or a non-staff profile) reads as false, so
// gated links stay hidden rather than leaking.
const can = (ctx, key) => Boolean(ctx.permissions?.[key]);

// The Canvas tab only shows while viewing the school's current (active) year
// (the Canvas permission plus this current-year check). The predicate itself
// lives in shared/schoolYear.js, since the navbar's inactive-year warning asks
// the same question.
const viewingCurrentYear = (ctx) =>
  isViewingCurrentYear(ctx.school, ctx.yearId);

export const NAV_TREE = [
  {
    id: "home",
    label: "Home",
    scope: "school",
    icon: HomeIcon,
    resolve: ({ schoolSlug }) => `/v2/school/${schoolSlug}`,
  },

  {
    id: "school-profile",
    label: "School Profile",
    scope: "school",
    v1: true,
    icon: BusinessIcon,
    // Shown with School Profile, or with Security as the fallback (which lands
    // the section on security-groups when the user lacks Profile).
    visible: (ctx) => can(ctx, "school_profile") || can(ctx, "security"),
    children: [
      {
        id: "school-profile-profile",
        label: "Profile",
        scope: "school",
        v1: true,
        resolve: v1Path("/school/profile"),
        visible: (ctx) => can(ctx, "school_profile"),
      },
      {
        id: "school-profile-security",
        label: "Security",
        scope: "school",
        v1: true,
        resolve: v1Path("/school/security-groups"),
        visible: (ctx) => can(ctx, "security"),
      },
      {
        id: "school-profile-grading",
        label: "Grading",
        scope: "school",
        v1: true,
        resolve: v1Path("/school/grading-scales"),
        keywords: ["grading scales"],
        visible: (ctx) => can(ctx, "grading_scales"),
      },
      {
        id: "school-profile-settings",
        label: "Settings",
        scope: "school",
        v1: true,
        resolve: v1Path("/school/settings"),
        visible: (ctx) => can(ctx, "settings"),
      },
      {
        id: "school-profile-gpa",
        label: "GPA",
        scope: "school",
        v1: true,
        resolve: v1Path("/school/gpa"),
        visible: (ctx) => can(ctx, "gpa"),
      },
      {
        // Groups is always shown once inside School Profile; the group-level
        // School Profile guard is the real gate.
        id: "school-profile-groups",
        label: "Groups",
        scope: "school",
        v1: true,
        resolve: v1Path("/school/groups"),
      },
      {
        // Custom Fields: always shown (see Groups).
        id: "school-profile-custom-fields",
        label: "Custom Fields",
        scope: "school",
        v1: true,
        resolve: v1Path("/school/custom-fields"),
      },
      {
        id: "school-profile-graduation-plans",
        label: "Graduation Plans",
        scope: "school",
        v1: true,
        resolve: v1Path("/school/graduation-plans"),
        visible: (ctx) => can(ctx, "graduation_plans"),
      },
      {
        id: "school-profile-canvas",
        label: "Canvas",
        scope: "school",
        v1: true,
        // The Canvas page is a layout route with no index: its content lives in
        // the setup/courses/logs children, so linking at the bare root renders
        // the sub-tab strip over an empty body. Land on Settings (setup), and
        // match on the root so the tab stays selected on the other two.
        resolve: v1Path("/school/canvas-integration/setup"),
        matchPath: v1Path("/school/canvas-integration"),
        keywords: ["lms", "integration"],
        visible: (ctx) => can(ctx, "canvas") && viewingCurrentYear(ctx),
      },
      {
        id: "school-profile-email-logs",
        label: "Email Logs",
        scope: "school",
        v1: true,
        resolve: v1Path("/school/email-logs"),
        visible: (ctx) => can(ctx, "email_logs"),
      },
      {
        // Documents: always shown (see Groups).
        id: "school-profile-documents",
        label: "Documents",
        scope: "school",
        v1: true,
        resolve: v1Path("/school/documents"),
      },
      {
        id: "school-profile-grid-edit",
        label: "Grid Edit",
        scope: "school",
        v1: true,
        resolve: v1Path("/grid-edit"),
        visible: (ctx) => can(ctx, "grid_edit"),
      },
      {
        id: "school-profile-notifications",
        label: "Notifications",
        scope: "school",
        v1: true,
        resolve: v1Path("/school/notifications"),
        visible: (ctx) => can(ctx, "notifications"),
      },
    ],
  },

  {
    id: "staff",
    label: "Staff",
    scope: "school",
    v1: true,
    icon: GroupsIcon,
    // Staff link gated on Staff List. Both sub-tabs were always shown
    // once inside, so they carry no own guard.
    visible: (ctx) => can(ctx, "staff_list"),
    children: [
      {
        id: "staff-list",
        label: "Staff",
        scope: "school",
        v1: true,
        resolve: v1Path("/staff/staffs"),
      },
      {
        id: "staff-temporary",
        label: "Temporary Staff",
        scope: "school",
        v1: true,
        resolve: v1Path("/staff/temporary-staff"),
      },
    ],
  },

  {
    id: "admissions",
    label: "Admissions",
    scope: "year",
    v1: true,
    icon: HowToRegIcon,
    // Admissions link gated on Admissions.
    visible: (ctx) => can(ctx, "admissions"),
    children: [
      {
        id: "admissions-students",
        label: "Students",
        scope: "year",
        v1: true,
        resolve: v1YearPath("/admissions/students"),
        keywords: ["inquiries", "applicants"],
        // Students/Inquiries tab gated on Student List.
        visible: (ctx) => can(ctx, "student_list"),
      },
      {
        id: "admissions-setup",
        label: "Setup",
        scope: "year",
        v1: true,
        resolve: v1YearPath("/admissions/setup"),
        keywords: ["processes", "forms"],
      },
      {
        id: "admissions-dashboard",
        label: "Dashboard",
        scope: "year",
        v1: true,
        resolve: v1YearPath("/admissions/dashboard"),
      },
      {
        id: "admissions-enrollments",
        label: "Enrollments",
        scope: "year",
        v1: true,
        resolve: v1YearPath("/admissions/enrollments"),
        keywords: ["enrolled", "unenrolled"],
      },
      {
        id: "admissions-approvals",
        label: "Approvals",
        scope: "year",
        v1: true,
        resolve: v1YearPath("/admissions/approvals"),
      },
      {
        id: "admissions-actions",
        label: "Actions",
        scope: "year",
        v1: true,
        resolve: v1YearPath("/admissions/actions"),
      },
    ],
  },

  {
    id: "courses",
    label: "Courses",
    scope: "school",
    v1: true,
    icon: MenuBookIcon,
    resolve: v1Path("/courses"),
    // Courses gated on the Courses permission AND a school-shape flag
    // (secondary students or elementary courses).
    visible: (ctx) =>
      can(ctx, "courses") &&
      Boolean(
        ctx.school?.has_secondary_students || ctx.school?.has_elementary_courses
      ),
  },

  {
    // Students had no permission guard - always shown.
    id: "students",
    label: "Students",
    scope: "year",
    v1: true,
    icon: PeopleIcon,
    resolve: ({ schoolSlug }) => `/school/${schoolSlug}/students`,
  },

  {
    id: "scheduling",
    label: "Scheduling",
    scope: "year",
    v1: true,
    icon: CalendarMonthIcon,
    // Scheduling link shown if the user has ANY of the four schedule
    // permissions; each tab is then individually gated below.
    visible: (ctx) =>
      can(ctx, "terms") ||
      can(ctx, "school_days") ||
      can(ctx, "day_template") ||
      can(ctx, "class_schedule"),
    children: [
      {
        id: "scheduling-terms",
        label: "Terms",
        scope: "year",
        v1: true,
        resolve: v1Path("/schedules/terms"),
        visible: (ctx) => can(ctx, "terms"),
      },
      {
        id: "scheduling-school-days",
        label: "School Days",
        scope: "year",
        v1: true,
        resolve: v1Path("/schedules/school-days"),
        visible: (ctx) => can(ctx, "school_days"),
      },
      {
        id: "scheduling-day-templates",
        label: "Day Templates",
        scope: "year",
        v1: true,
        resolve: v1Path("/schedules/day-templates"),
        visible: (ctx) => can(ctx, "day_template"),
      },
      {
        id: "scheduling-class-schedules",
        label: "Class Schedules",
        scope: "year",
        v1: true,
        resolve: v1Path("/schedules/class-schedules"),
        visible: (ctx) => can(ctx, "class_schedule"),
      },
    ],
  },

  {
    id: "reports",
    label: "Reports",
    scope: "year",
    v1: true,
    icon: AssessmentIcon,
    // whole Reports screen gated once on Reporting Screen; the
    // individual reports below were never separately gated.
    visible: (ctx) => can(ctx, "report_screen"),
    children: [
      {
        id: "reports-report-assignments",
        label: "Assignments",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/report-assignments"),
      },
      {
        id: "reports-attendance",
        label: "Attendance",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/Attendance"),
      },
      {
        id: "reports-attendance-summary",
        label: "Attendance Summary",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/attendance-summary"),
      },
      {
        id: "reports-birthdays",
        label: "Birthdays",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/birthdays"),
      },
      {
        id: "reports-class-roster",
        label: "Class Roster",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/class-roster"),
      },
      {
        id: "reports-emergency-info",
        label: "Emergency Info",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/emergency-info"),
      },
      {
        id: "reports-enrollment",
        label: "Enrollment",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/enrollment"),
      },
      {
        id: "reports-homeroom-roster",
        label: "Homeroom Roster",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/homeroom-roster"),
      },
      {
        id: "reports-parent-contacts",
        label: "Parent Contacts",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/parent-contacts"),
      },
      {
        id: "reports-student-progress",
        label: "Student Progress",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/student-progress"),
      },
      {
        id: "reports-school-calendar",
        label: "School Calendar",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/school-calendar"),
      },
      {
        id: "reports-student-list",
        label: "Student List",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/student-list"),
      },
      {
        id: "reports-progress-report",
        label: "Progress Report",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/progress-report"),
      },
      {
        id: "reports-student-parent-report",
        label: "Student Parent Report",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/student-parent-report"),
      },
      {
        id: "reports-schedule-report",
        label: "Schedule Report",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/schedule-report"),
      },
      {
        id: "reports-class-attendance-report",
        label: "Class Attendance Report",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/class-attendance-report"),
      },
      {
        id: "reports-grade-rank-eligibility",
        label: "Grade Rank Eligibility",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/grade-rank-eligibility"),
      },
      {
        id: "reports-truancy",
        label: "Truancy Report",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/truancy-report"),
      },
      {
        id: "reports-school-minutes",
        label: "School Minutes",
        scope: "year",
        v1: true,
        resolve: v1Path("/reports/school-minutes"),
      },
    ],
  },

  {
    id: "attendance",
    label: "Attendance",
    scope: "school",
    v1: true,
    icon: EventAvailableIcon,
    resolve: v1Path("/attendance"),
    // Attendance gated on Day Attendance.
    visible: (ctx) => can(ctx, "attendance"),
  },

  {
    id: "classes",
    label: "Classes",
    scope: "year",
    v1: true,
    icon: ClassIcon,
    resolve: ({ schoolSlug }) => `/school/${schoolSlug}/current-classes`,
    // Classes gated on My Classes.
    visible: (ctx) => can(ctx, "my_classes"),
  },

  {
    id: "subjects",
    label: "Subjects",
    scope: "year",
    icon: SubjectIcon,
    resolve: ({ schoolSlug, yearId }) =>
      `/v2/school/${schoolSlug}/${yearId}/subjects`,
    keywords: ["gradebook"],
    // Subjects gated on My Subjects.
    visible: (ctx) => can(ctx, "my_subjects"),
  },

  {
    id: "assignments",
    label: "Assignments",
    scope: "year",
    v1: true,
    icon: AssignmentIcon,
    resolve: ({ schoolSlug }) => `/school/${schoolSlug}/assignments`,
    // Assignments gated on Classes > Assignments.
    visible: (ctx) => can(ctx, "classes_assignments"),
  },

  {
    id: "report-cards",
    label: "Report Cards",
    scope: "year",
    v1: true,
    icon: AssignmentTurnedInIcon,
    // Report Cards required MANAGE specifically (view-only did not show).
    // The group lands on its first child (Dashboard).
    visible: (ctx) => can(ctx, "report_cards"),
    children: [
      {
        id: "report-cards-dashboard",
        label: "Dashboard",
        scope: "year",
        v1: true,
        resolve: ({ schoolSlug }) =>
          `/school/${schoolSlug}/report-cards/dashboard`,
      },
      {
        id: "report-cards-report-cards",
        label: "Report Cards",
        scope: "year",
        v1: true,
        resolve: ({ schoolSlug }) => `/school/${schoolSlug}/report-cards`,
      },
      {
        id: "report-cards-sbg",
        label: "SBG Report Cards",
        scope: "year",
        resolve: ({ schoolSlug, yearId }) =>
          `/v2/school/${schoolSlug}/${yearId}/sbg/report-cards`,
        keywords: ["standards based grading", "sbg"],
        // Same compound the report-cards page gates the SBG tab on (school SBG
        // enabled AND either SBG manage permission or the user's employment SBG
        // flag), precomputed server-side into `sbg_report_cards`.
        visible: (ctx) => can(ctx, "sbg_report_cards"),
      },
    ],
  },

  {
    id: "social-development",
    label: "Social Development",
    scope: "year",
    v1: true,
    icon: PsychologyIcon,
    resolve: ({ schoolSlug }) => `/school/${schoolSlug}/social-development`,
    keywords: ["work ethic"],
    // shown on the Social Development/Work Ethic permission OR manage on
    // All Students.
    visible: (ctx) =>
      can(ctx, "social_development") || can(ctx, "all_students"),
  },

  {
    id: "pk-tracker",
    label: "Pre-K Development Tracker",
    scope: "year",
    v1: true,
    icon: ChildCareIcon,
    resolve: ({ schoolSlug }) => `/school/${schoolSlug}/pk-development-tracker`,
    keywords: ["prek", "pre-k"],
    // PK Tracker permission AND the has_pk_tracker school flag.
    visible: (ctx) =>
      can(ctx, "pk_tracker") && Boolean(ctx.school?.has_pk_tracker),
  },
];

function walk(nodes, ancestors = []) {
  return nodes.flatMap((node) => {
    const chain = [...ancestors, node];
    const breadcrumb = chain.map((n) => n.label);
    const children = node.children ? walk(node.children, chain) : [];
    return [{ node, ancestors, breadcrumb }, ...children];
  });
}

export const NAV_INDEX = walk(NAV_TREE);

// A node's own visibility predicate, ignoring children. Nodes without a
// `visible` predicate are always shown; ones that declare one return false to be
// filtered out entirely (not just disabled).
function ownVisible(node, ctx) {
  return typeof node.visible === "function" ? Boolean(node.visible(ctx)) : true;
}

// Node-level visibility used by the menus. A leaf is visible when its own
// predicate passes. A group is visible only when its own predicate passes AND at
// least one child is visible - so a permitted section whose every tab is gated
// away never renders an empty, dead header.
export function nodeVisible(node, ctx) {
  if (!ownVisible(node, ctx)) return false;
  if (node.children?.length) {
    return node.children.some((child) => nodeVisible(child, ctx));
  }
  return true;
}

// Whether a node is actually reachable in this context: its own predicate AND
// every ancestor's predicate must pass. Takes a NAV_INDEX entry ({ node,
// ancestors }). Used by search and bookmark filtering so a gated section never
// surfaces its links (or a saved bookmark) when the user lacks access at the
// selected school. scopeReady is separate: it only disables, it never omits.
export function nodeChainVisible(entry, ctx) {
  return (
    ownVisible(entry.node, ctx) &&
    entry.ancestors.every((ancestor) => ownVisible(ancestor, ctx))
  );
}

// The navigable leaf for any node. A leaf returns itself; a group (no own
// `resolve`) walks down its first child until it reaches a node that carries a
// route. When `ctx` is supplied, children failing their visibility predicate are
// skipped, so a group lands on its first PERMITTED page (e.g. School Profile ->
// Security when the user lacks Profile). Returns null if nothing down the chain
// resolves. Callers that need a URL or the `v2` flag for a node (navigation,
// search, bookmarks) go through this so groups transparently open their section.
export function navTarget(node, ctx = null) {
  let target = node;
  while (target && !target.resolve && target.children?.length) {
    const kids = ctx
      ? target.children.filter((child) => nodeVisible(child, ctx))
      : target.children;
    if (!kids.length) return null;
    [target] = kids;
  }
  return target?.resolve ? target : null;
}

// The live URL for a node in the given context, or null if it has no navigable
// target. Groups inherit their first (visible) child's URL (see navTarget).
export function nodeUrl(node, ctx) {
  const target = navTarget(node, ctx);
  return target ? target.resolve(ctx) : null;
}

// The path that identifies "the page this node owns", for deciding which tab in
// a strip is the active one. Defaults to the node's own URL, which is the root
// of its routes for almost every node - a graduation plan detail page sits under
// the Graduation Plans URL, so a prefix match keeps that tab selected with no
// extra declaration. A node whose landing URL is deeper than its root (Canvas
// must land on .../canvas-integration/setup, but owns every
// .../canvas-integration/* route) declares `matchPath` for the root instead.
export function nodeMatchPath(node, ctx) {
  const target = navTarget(node, ctx);
  if (!target) return null;
  return (target.matchPath ?? target.resolve)(ctx);
}

// Whether the context carries everything a node's URL needs. `school` links
// only need the slug; `year` links also need the year id. No link takes a term:
// term-scoped pages default or redirect to their own term on landing, so the
// nav never puts one on the URL and never waits for one.
export function scopeReady(node, ctx) {
  if (node.scope === "school") return Boolean(ctx.schoolId || ctx.schoolSlug);
  if (node.scope === "year") return Boolean(ctx.yearId);
  return true;
}

// Resolve a group id to its visible sub-links for a header tab strip. Looks the
// group up in NAV_INDEX, keeps only the children the user may see at the current
// school (nodeVisible: own predicate + a group's own visible descendant), and
// returns them as plain descriptors. This is the single-source counterpart to a
// hand-built tab array: a header passes `report-cards` (or any group id) and
// gets exactly the tabs the nav menu would show, permission-gated the same way.
//
// Each descriptor carries the live `url` (nodeUrl), the `match` path an active-
// tab check should compare against (nodeMatchPath - the same as `url` unless the
// node lands deeper than the routes it owns), a `v1` flag (the resolved target's
// route lives on the older frontend, so navigating there is a full page load
// rather than an in-place SPA nav), and a `disabled` flag for a link whose scope
// isn't ready yet (scopeReady only disables, it never omits - matching the nav
// menu). Consumers turn these into onClick/href as they see fit. Returns [] for
// an unknown id or a leaf node (no children to spread into tabs).
export function resolveMenu(id, ctx) {
  const entry = NAV_INDEX.find((e) => e.node.id === id);
  const children = entry?.node.children;
  if (!children) return [];
  return children
    .filter((child) => nodeVisible(child, ctx))
    .map((child) => ({
      key: child.id,
      label: child.label,
      url: nodeUrl(child, ctx),
      match: nodeMatchPath(child, ctx),
      v1: Boolean(navTarget(child, ctx)?.v1),
      disabled: !scopeReady(child, ctx),
    }));
}

// Where the school-year select should land, given the page currently open.
//
// `path` is the current pathname already rebased onto the year being switched TO
// (only the year segment swapped); `ctx` carries that same year. A registry
// route is year-agnostic by construction - it is built from the slug and the
// year id alone - so any registry URL containing the rebased path is a valid
// destination in the new year. Anything BELOW one is a record id (a class, a
// student) that belongs to the year being left, so the deepest registry URL
// wins and the remainder of the path is dropped. No match at all (a deep record
// route with no registry ancestor) falls back to the school year root.
//
// Only SPA nodes are considered: `v1` routes live on the older frontend and
// never share this URL space.
export function yearSwitchPath(path, ctx) {
  const root = `/v2/school/${ctx.schoolSlug}/${ctx.yearId}`;
  const matches = NAV_INDEX.filter(({ node }) => node.resolve && !node.v1)
    .map(({ node }) => node.resolve(ctx))
    // School-scoped links (Home) sit above the year root and would match every
    // path; the select only renders on year-scoped pages, so ignore them.
    .filter((url) => url.startsWith(root))
    .filter((url) => path === url || path.startsWith(`${url}/`));
  matches.sort((a, b) => b.length - a.length);
  return matches[0] ?? root;
}

function rankMatch(q, node, breadcrumb) {
  const label = node.label.toLowerCase();
  if (label.startsWith(q)) return 0;
  if (label.includes(q)) return 1;
  if (breadcrumb.join(" › ").toLowerCase().includes(q)) return 2;
  if ((node.keywords || []).join(" ").toLowerCase().includes(q)) return 3;
  return -1;
}

export function searchNav(query, ctx) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const results = NAV_INDEX.flatMap((entry) => {
    const { node, breadcrumb } = entry;
    // A hit is shown only when the node and every ancestor pass their own
    // visibility predicate - a permission-gated section must not surface its
    // children through search when its group is hidden.
    if (!nodeChainVisible(entry, ctx)) return [];
    if (!scopeReady(node, ctx)) return [];
    const rank = rankMatch(q, node, breadcrumb);
    return rank >= 0 ? [{ node, breadcrumb, rank }] : [];
  });
  results.sort((a, b) =>
    a.breadcrumb.join(" ").localeCompare(b.breadcrumb.join(" "))
  );
  return results;
}
